import type { HelpGuide } from '../types';

export const apGuide: HelpGuide = {
  key: 'ap',
  title: 'เจ้าหนี้การค้า (Accounts Payable)',
  subtitle: 'เมนู: ลูกหนี้ / เจ้าหนี้ ▸ เจ้าหนี้การค้า',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/ap/คู่มือการใช้งาน_เจ้าหนี้การค้า_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “เจ้าหนี้การค้า (Accounts Payable)” ใช้บันทึกและติดตามหนี้สินที่บริษัทต้องจ่ายให้ผู้ขาย/ผู้รับเงิน ตั้งแต่บันทึกใบสำคัญจ่าย (บิลค่าใช้จ่าย) ผ่านการอนุมัติ ลงบัญชี จ่ายเงิน (พร้อมหักภาษี ณ ที่จ่าย) ออกใบลด/เพิ่มหนี้ ไปจนถึงรายงานอายุหนี้และประวัติการจ่าย ทั้งหมดอยู่ในหน้าจอเดียว',
        },
        {
          kind: 'callout',
          text: 'หลักการบันทึกบัญชี: บันทึกบิล → Dr ค่าใช้จ่าย/ภาษีซื้อ / Cr เจ้าหนี้การค้า · จ่ายเงิน → Dr เจ้าหนี้การค้า / Cr เงินสด-ธนาคาร (หากหักภาษี ณ ที่จ่าย → Cr ภาษีหัก ณ ที่จ่ายค้างจ่าย) · ใบลดหนี้ → ลดยอดเจ้าหนี้ · ใบเพิ่มหนี้ → เพิ่มยอดเจ้าหนี้',
        },
        { kind: 'para', text: 'หน้าจอแบ่งเป็น 11 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ทั้งหมด — ', text: 'ใบสำคัญจ่ายทุกสถานะ (แท็บเริ่มต้น)' },
            { lead: 'รอจ่าย — ', text: 'บิลที่ลงบัญชีแล้วและยังค้างจ่าย' },
            {
              lead: 'รออนุมัติ — ',
              text: 'บิลที่รอการอนุมัติ (สายอนุมัติกลาง หรืออนุมัติแบบ manual)',
            },
            { lead: 'เกินกำหนด — ', text: 'บิลที่ลงบัญชีแล้วและเลยกำหนดจ่าย' },
            { lead: 'จ่ายแล้ว — ', text: 'บิลที่จ่ายครบแล้ว' },
            { lead: 'อายุหนี้ — ', text: 'รายงานอายุหนี้รายผู้ขาย 5 ช่วง' },
            { lead: 'ประวัติการจ่าย — ', text: 'รายการจ่ายเงินทุกสถานะ' },
            { lead: 'รอบจ่าย — ', text: 'รอบจ่ายเงินแบบรวมหลายผู้ขาย' },
            { lead: 'ใบลด/เพิ่มหนี้ — ', text: 'ใบลดหนี้ / ใบเพิ่มหนี้' },
            { lead: 'บิลประจำ — ', text: 'แม่แบบบิลที่เกิดซ้ำเป็นรอบ' },
            { lead: 'แดชบอร์ด — ', text: 'ตัวชี้วัด (KPI) ภาพรวมแบบอ่านอย่างเดียว' },
          ],
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบนสุด (ปรับตามตัวกรองสาขา/หน่วยงาน):' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ใบสำคัญรวม — ', text: 'มูลค่าบิลทั้งหมด พร้อมจำนวนฉบับ (YTD)' },
            { lead: 'จ่ายแล้ว — ', text: 'ยอดที่จ่ายไปแล้ว พร้อมร้อยละเทียบยอดบิลรวม' },
            {
              lead: 'คงค้างจ่าย — ',
              text: 'ยอดเจ้าหนี้คงค้าง พร้อมระยะเวลาจ่ายชำระเฉลี่ย (DPO เฉลี่ย … วัน)',
            },
            { lead: 'เกินกำหนด — ', text: 'ยอดบิลที่เลยกำหนดจ่าย พร้อมจำนวนฉบับ' },
          ],
        },
        { kind: 'para', text: 'ปุ่มมุมขวาบน:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'นำเข้า — ',
              text: 'นำเข้าใบสำคัญจ่ายจากไฟล์ .xlsx (1 แถว = 1 ใบสำคัญ ที่มีรายการเดียว)',
            },
            { lead: 'ส่งออก — ', text: 'ส่งออกรายการที่กรองอยู่เป็นไฟล์ .xlsx' },
            { lead: 'อนุมัติ (จำนวน) — ', text: 'กระโดดไปแท็บ “รออนุมัติ” เพื่อดูบิลที่รออนุมัติ' },
            {
              lead: 'บันทึกใบสำคัญจ่าย (ปุ่มสีแดง) — ',
              text: 'เปิดฟอร์มสร้างใบสำคัญจ่ายฉบับใหม่',
            },
          ],
        },
        {
          kind: 'para',
          text: 'เหนือตารางมีตัวกรอง สาขา + หน่วยงาน และช่อง ค้นหา เลขที่ใบสำคัญ / ผู้ขาย คอลัมน์หลักของตารางบิล: เลขที่ใบสำคัญ · ผู้ขาย/ผู้รับเงิน · รายละเอียด · วันที่ · กำหนดจ่าย · มูลค่ารวม · จ่ายแล้ว · คงค้าง · สถานะ — คลิกเลขที่ใบสำคัญเพื่อเปิดรายละเอียด',
        },
      ],
    },
    {
      id: 'all-bills',
      heading: '3. แท็บทั้งหมด — ตารางใบสำคัญจ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ทั้งหมด แสดงใบสำคัญจ่ายทุกสถานะในตารางเดียว แต่ละแถวบอกยอด คงค้าง (= มูลค่ารวม + ใบเพิ่ม/ลดหนี้ − จ่ายแล้ว) และป้ายสถานะ บิลที่อยู่ในช่วงรับส่วนลดจ่ายเร็วจะมีป้าย “ส่วนลด …% ถึง …” กำกับ',
        },
        {
          kind: 'para',
          text: 'ปุ่มในแต่ละแถว (เฉพาะบิลที่ลงบัญชีแล้ว): จ่ายเงิน (บันทึกการจ่ายทีละบิล) และ ใบลด/เพิ่มหนี้ (ออกใบลด/เพิ่มหนี้ผูกกับบิลนั้น) เมื่อ ติ๊กเลือก ตั้งแต่ 1 แถวขึ้นไป จะมีแถบเครื่องมือ จ่ายเงินที่เลือก (เปิดตัวช่วยสร้างรอบจ่าย) และ ล้างการเลือก',
        },
        {
          kind: 'image',
          src: '/help/ap/01-overview.png',
          caption:
            'ภาพรวมเจ้าหนี้การค้า — การ์ด KPI, แท็บทั้งหมด และตารางใบสำคัญจ่ายทุกสถานะพร้อมยอดคงค้างและป้ายสถานะ',
        },
      ],
    },
    {
      id: 'pending',
      heading: '4. แท็บรออนุมัติ',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ รออนุมัติ รวมบิลที่ยังไม่ลงบัญชีและรอการอนุมัติ มี 2 รูปแบบ: บิลที่เข้าสายอนุมัติกลาง (4 ตา) จะมีปุ่ม ดำเนินการอนุมัติ เปิดแผงไทม์ไลน์การอนุมัติ (ผู้จัดทำ → ผู้สอบทาน → ผู้อนุมัติ) ส่วนบิลที่อนุมัติแบบ manual จะมีปุ่ม อนุมัติ (ลงบัญชีทันที) และ ปฏิเสธ (ระบุเหตุผลแล้วเปลี่ยนสถานะเป็น “ยกเลิก”)',
        },
        {
          kind: 'callout',
          text: 'การอนุมัติ การปฏิเสธ และการตีกลับ เป็นไปตามสิทธิ์ของผู้ใช้ ผู้จัดทำบิลกับผู้อนุมัติต้องเป็นคนละคน (หลักการแบ่งแยกหน้าที่ Segregation of Duties)',
        },
        {
          kind: 'image',
          src: '/help/ap/02-pending.png',
          caption:
            'แท็บรออนุมัติ — บิลที่เข้าสายอนุมัติกลาง (ปุ่มดำเนินการอนุมัติ) และบิล manual (ปุ่มอนุมัติ/ปฏิเสธ)',
        },
      ],
    },
    {
      id: 'create-bill',
      heading: '5. การบันทึกใบสำคัญจ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม บันทึกใบสำคัญจ่าย แล้วกรอก: ผู้ขาย · วันที่บิล · กำหนดจ่าย · สาขา/หน่วยงาน (ถ้ามี) · เลขที่บิลผู้ขาย · คำอธิบาย · เงื่อนไขชำระ (ช่วยคำนวณกำหนดจ่ายและช่วงส่วนลดจ่ายเร็วให้อัตโนมัติ) จากนั้นเพิ่ม รายการค่าใช้จ่าย ทีละบรรทัด (คำอธิบาย · จำนวน · ราคาต่อหน่วย · รหัสบัญชีค่าใช้จ่าย)',
        },
        {
          kind: 'para',
          text: 'ด้านล่างมีแถบสรุป ยอดก่อนภาษี · อัตรา VAT % · ภาษีมูลค่าเพิ่ม · รวมเงิน ระบบมีการตรวจซ้ำ (เตือนเมื่อพบบิลผู้ขาย+เลขที่บิลซ้ำกับที่เคยบันทึก) และมีตัวเลือก ผูกโครงการ CIP (สำหรับงานระหว่างก่อสร้าง) เลือก โหมดการบันทึก: “โพสต์ JV ทันที (POSTED)” เพื่อลงบัญชีเลย หรือ “รออนุมัติ (PENDING — ไม่ลง GL)” เพื่อให้บิลเข้าสถานะ “รออนุมัติ” แล้วกดปุ่ม บันทึก เพื่อสร้าง',
        },
        {
          kind: 'image',
          src: '/help/ap/03-create-bill.png',
          caption:
            'ฟอร์มบันทึกใบสำคัญจ่าย — ผู้ขาย/รายการค่าใช้จ่าย, เงื่อนไขชำระ/ส่วนลด และแถบสรุป VAT/ยอดรวม',
        },
      ],
    },
    {
      id: 'bill-detail',
      heading: '6. รายละเอียดใบสำคัญจ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'คลิก เลขที่ใบสำคัญ ในตารางเพื่อเปิดหน้ารายละเอียดแบบอ่านอย่างเดียว แสดงข้อมูลครบ: การ์ดสรุปบิล (ผู้ขาย · กำหนดจ่าย · สาขา/หน่วยงาน · สถานะ) · ตารางรายการค่าใช้จ่าย · การแยกภาษีมูลค่าเพิ่ม (ยอดก่อนภาษี/อัตรา VAT/ภาษี/รวม)',
        },
        {
          kind: 'para',
          text: 'หากบิลมีการจ่ายแล้วจะแสดงรายการการจ่ายและใบหักภาษี ณ ที่จ่าย (50 ทวิ) พร้อมปุ่มพิมพ์ใบรับรอง · หากเข้าสายอนุมัติกลางจะแสดงไทม์ไลน์การอนุมัติ · หากมีใบลด/เพิ่มหนี้จะแสดงรายการให้คลิกเปิดดูได้ · กรณีภาษีซื้อรอเครดิต (deferred VAT) จะมีปุ่มสำหรับรับรู้ภาษีซื้อเมื่อได้รับใบกำกับภาษีตัวจริง',
        },
        {
          kind: 'image',
          src: '/help/ap/04-bill-detail.png',
          caption:
            'รายละเอียดใบสำคัญจ่าย — รายการค่าใช้จ่าย, การแยก VAT, ใบลด/เพิ่มหนี้ที่ผูกอยู่ และใบ 50 ทวิ',
        },
      ],
    },
    {
      id: 'credit-note',
      heading: '7. การออกใบลดหนี้ / ใบเพิ่มหนี้',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ใบลด/เพิ่มหนี้ ที่แถวบิล (หรือจากหน้ารายละเอียด) เพื่อสร้างใบลดหนี้ (CREDIT — ลดยอดเจ้าหนี้) หรือใบเพิ่มหนี้ (DEBIT — เพิ่มยอดเจ้าหนี้) ที่ผูกกับบิลนั้น เลือก ประเภท แล้วระบุ วันที่ · เหตุผล (เช่น ลดราคา / ส่วนลด / สินค้าชำรุด / รับไม่ครบ / ส่งคืนสินค้า / ผู้ขายเรียกเก็บเกิน / ผู้ขายเรียกเก็บขาด / ค่าใช้จ่ายเพิ่มเติม / ยกเลิกรายการ) · หมายเหตุเพิ่มเติม',
        },
        {
          kind: 'para',
          text: 'เพิ่มรายการทีละบรรทัด แล้วดูแถบสรุป ยอดก่อนภาษี/VAT (อัตราตามบิลต้นทาง)/รวมเงิน กรณีใบลดหนี้ ยอดต้องไม่เกินยอดคงค้างของบิล เมื่อกด บันทึก ระบบจะส่งเข้าสายอนุมัติกลางและแสดงไทม์ไลน์การอนุมัติ',
        },
        {
          kind: 'image',
          src: '/help/ap/05-credit-note.png',
          caption: 'ฟอร์มออกใบลดหนี้/ใบเพิ่มหนี้ — เลือกประเภท, เหตุผล, รายการ และแถบสรุปยอด',
        },
      ],
    },
    {
      id: 'payment-run-1',
      heading: '8. รอบจ่าย — ขั้นที่ 1 เลือกใบสำคัญที่จะจ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'รอบจ่ายใช้จ่ายบิลหลายฉบับ/หลายผู้ขายในครั้งเดียว เริ่มได้จากการ ติ๊กเลือกบิล ในตารางแล้วกด จ่ายเงินที่เลือก ระบบจะเปิดตัวช่วย 3 ขั้น ขั้นที่ 1 ให้เลือก/ยืนยันใบสำคัญที่จะจ่าย (ติ๊กเลือกได้หลายรายการ จัดกลุ่มตามผู้ขาย) แล้วกด ถัดไป',
        },
        {
          kind: 'image',
          src: '/help/ap/06-payment-run-step1.png',
          caption: 'รอบจ่ายขั้นที่ 1 — เลือกใบสำคัญที่จะจ่าย (จัดกลุ่มตามผู้ขาย)',
        },
      ],
    },
    {
      id: 'payment-run-3',
      heading: '9. รอบจ่าย — ขั้นที่ 3 สรุปและยืนยัน',
      blocks: [
        {
          kind: 'para',
          text: 'ขั้นที่ 2 ปรับยอดจ่ายและตั้งค่าหักภาษี ณ ที่จ่ายต่อรายการ (ระบุประเภทเงินได้และอัตรา) ขั้นที่ 3 เลือก บัญชีเงินสด/ธนาคาร · วันที่จ่าย · หมายเหตุ และเลือกช่อง e-WHT ได้ (กรณีธนาคารออกใบรับรองหัก ณ ที่จ่ายแทน) พร้อมสรุปยอดรวม · ภาษีหัก ณ ที่จ่าย · ยอดสุทธิ',
        },
        {
          kind: 'para',
          text: 'กดปุ่ม ส่งอนุมัติ เพื่อสร้างรอบจ่ายและส่งเข้าสายอนุมัติกลาง เมื่ออนุมัติครบ ระบบจึงลงบัญชีจ่ายเงินและสร้างรายการจ่ายรายผู้ขายให้อัตโนมัติ',
        },
        {
          kind: 'image',
          src: '/help/ap/07-payment-run-step3.png',
          caption: 'รอบจ่ายขั้นที่ 3 — บัญชีเงินสด/e-WHT และสรุปยอดรวม/หัก ณ ที่จ่าย/ยอดสุทธิ',
        },
      ],
    },
    {
      id: 'aging',
      heading: '10. แท็บอายุหนี้',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ อายุหนี้ แสดงรายงานอายุหนี้รายผู้ขาย แบ่งยอดคงค้างเป็น 5 ช่วง: ยังไม่ครบกำหนด · 1-30 วัน · 31-60 วัน · 61-90 วัน · 90+ วัน พร้อมคอลัมน์ รวม ต่อผู้ขายและแถวรวมทั้งสิ้น ใช้ดูว่าหนี้ค้างจ่ายกระจุกอยู่ช่วงใดและกับผู้ขายรายใด มีปุ่มส่งออกเป็นไฟล์ .xlsx',
        },
        {
          kind: 'image',
          src: '/help/ap/08-aging.png',
          caption: 'แท็บอายุหนี้ — รายงานอายุหนี้รายผู้ขาย 5 ช่วง พร้อมแถวรวม',
        },
      ],
    },
    {
      id: 'payments',
      heading: '11. แท็บประวัติการจ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ประวัติการจ่าย รวมรายการจ่ายเงินทุกสถานะ คอลัมน์: เลขที่ใบสำคัญ · วันที่จ่าย · ผู้ขาย · บิลอ้างอิง · ยอดจ่าย · สถานะ (ร่าง / รออนุมัติ / อนุมัติแล้ว) คลิกที่แถวเพื่อเปิดแผงการอนุมัติของรายการจ่ายนั้น (สำหรับการจ่ายทีละบิลที่เข้าสายอนุมัติ 4 ตา)',
        },
        {
          kind: 'image',
          src: '/help/ap/09-payments.png',
          caption: 'แท็บประวัติการจ่าย — รายการจ่ายเงิน, สถานะ และเข้าสู่แผงอนุมัติได้จากแถว',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '12. สรุปสถานะ',
      blocks: [
        { kind: 'para', text: 'สถานะใบสำคัญจ่าย (บิล):' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['รออนุมัติ', 'รอการอนุมัติ (manual หรือสายอนุมัติกลาง 4 ตา) ยังไม่ลงบัญชี'],
            ['รอจ่าย', 'อนุมัติและลงบัญชีแล้ว ยังค้างจ่าย'],
            ['เกินกำหนด', 'ลงบัญชีแล้วและเลยกำหนดจ่าย'],
            ['จ่ายแล้ว', 'จ่ายครบเต็มจำนวนแล้ว'],
            ['ยกเลิก', 'ถูกปฏิเสธก่อนลงบัญชี หรือถูกยกเลิก'],
          ],
        },
        { kind: 'para', text: 'สถานะรายการจ่าย / รอบจ่าย / ใบลด-เพิ่มหนี้:' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['ร่าง', 'สร้างแล้วแต่ยังไม่ส่งอนุมัติ ยังไม่ลงบัญชี'],
            ['รออนุมัติ', 'ส่งเข้าสายอนุมัติแล้ว รอผู้อนุมัติ'],
            ['อนุมัติแล้ว', 'อนุมัติครบและลงบัญชีแล้ว'],
            ['ตีกลับ', 'ถูกตีกลับให้แก้ไขแล้วส่งใหม่'],
            ['ปฏิเสธ / ยกเลิก', 'ถูกปฏิเสธหรือยกเลิก (สิ้นสุด)'],
            ['ลงบัญชีแล้ว', 'ใบลด/เพิ่มหนี้ที่อนุมัติและลงบัญชีแล้ว'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '13. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'บันทึกบิลแล้วทำไมยังไม่เห็นยอดเจ้าหนี้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'หากเลือกโหมดการบันทึก “รออนุมัติ (PENDING — ไม่ลง GL)” บิลจะอยู่สถานะ “รออนุมัติ” ต้องอนุมัติที่แท็บรออนุมัติก่อน ยอดจึงจะลงบัญชี',
            },
          ],
        },
        { kind: 'para', text: 'จะจ่ายหลายบิลพร้อมกันทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ติ๊กเลือกบิล (สถานะรอจ่าย) ตั้งแต่ 1 ฉบับขึ้นไป แล้วกด “จ่ายเงินที่เลือก” เพื่อเปิดตัวช่วยสร้างรอบจ่าย',
            },
          ],
        },
        { kind: 'para', text: 'รับส่วนลดจ่ายเร็วได้เมื่อไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เมื่อบิลยังอยู่ในช่วงส่วนลด (ดูป้าย “ส่วนลด …% ถึง …”) และจ่ายเต็มจำนวน จะมีตัวเลือกรับส่วนลดในหน้าจ่ายเงิน',
            },
          ],
        },
        { kind: 'para', text: 'ใบลดหนี้ใส่ยอดเกินบิลได้ไหม?' },
        {
          kind: 'bullets',
          items: [{ text: 'ไม่ได้ — ยอดใบลดหนี้ต้องไม่เกินยอดคงค้างของบิลต้นทาง' }],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '14. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ใบสำคัญจ่าย — ',
              text: 'เอกสารบันทึกหนี้สิน/ค่าใช้จ่ายค้างจ่ายต่อผู้ขาย (บิล AP)',
            },
            {
              lead: 'คงค้าง — ',
              text: 'ยอดที่ยังต้องจ่าย = มูลค่ารวม + ใบเพิ่มหนี้ − ใบลดหนี้ − จ่ายแล้ว',
            },
            {
              lead: 'DPO — ',
              text: 'ระยะเวลาจ่ายชำระเฉลี่ย (Days Payable Outstanding) จำนวนวันเฉลี่ยที่บริษัทใช้ก่อนจ่ายเจ้าหนี้',
            },
            {
              lead: 'ภาษีหัก ณ ที่จ่าย (50 ทวิ) — ',
              text: 'ภาษีที่หักจากผู้รับเงินตอนจ่าย พร้อมออกหนังสือรับรอง (50 ทวิ) และนำส่ง ภงด.3/53',
            },
            {
              lead: 'e-WHT — ',
              text: 'การหักภาษี ณ ที่จ่ายแบบให้ธนาคารออกใบรับรองแทน (ไม่พิมพ์ 50 ทวิ เอง)',
            },
            {
              lead: 'รอบจ่าย — ',
              text: 'การรวมจ่ายบิลหลายฉบับ/หลายผู้ขายในครั้งเดียวผ่านตัวช่วย 3 ขั้น',
            },
            {
              lead: 'ใบลดหนี้ / ใบเพิ่มหนี้ — ',
              text: 'เอกสารปรับยอดเจ้าหนี้ลง (CREDIT) หรือเพิ่ม (DEBIT) ผูกกับบิลต้นทาง',
            },
            {
              lead: 'สายอนุมัติกลาง (4 ตา) — ',
              text: 'กระบวนการอนุมัติ ผู้จัดทำ → ผู้สอบทาน → ผู้อนุมัติ โดยต้องเป็นคนละคน',
            },
          ],
        },
      ],
    },
  ],
};
